import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCenterSubscription } from "../../../subscription";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import { useCenterPaymentsEnabled, Center } from "../../../center";
import CenterAdminNav from "./CenterAdminNav";

interface Props {
    center: Center;
    title?: string;
    children: ReactNode;
}

const PANEL_ID = "center-admin-panel";

/** Every administration screen, in the same frame. */
export default function CenterAdminShell({ center, title, children }: Props) {
    const { t } = useTranslation();
    const { enabled: paymentsEnabled } = useCenterPaymentsEnabled(center.id);
    const { data: subscription } = useCenterSubscription(center.id);

    return (
        <>
            <PageMeta title={title ?? center.name} description={t("center.admin.title")} />
            <div className="mx-auto flex max-w-5xl flex-col">
                <div className="mb-6">
                    {/* The way back. An account can hold both roles, and the
                        two surfaces are places rather than modes. */}
                    <Link
                        to="/"
                        className="mb-2 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <ArrowLeft size={16} />
                        {t("center.nav.backToPractice")}
                    </Link>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-semibold text-content-primary">
                            {center.name}
                        </h1>
                        {/* What this centre is on. A centre that is free for
                            ever is not on the Plus plan in the sense the word
                            is used everywhere else — nobody is billed for it
                            and there is nothing to cancel — so saying "Plus"
                            would send an administrator looking for a
                            subscription that does not exist. */}
                        {subscription && (
                            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                                {subscription.earlyAdopter
                                    ? t("subscription.status.earlyAdopterPlan")
                                    : t(
                                          // The size of the seats it pays for.
                                          subscription.subscription?.heldTier === "max"
                                              ? "subscription.max.title"
                                              : "subscription.plus.title"
                                      )}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {center.type === "training"
                            ? t("center.admin.subtitleTraining")
                            : t("center.admin.subtitleTherapy")}
                    </p>
                </div>
                <CenterAdminNav
                    type={center.type}
                    panelId={PANEL_ID}
                    showPayments={paymentsEnabled}
                />
                {/* No gap above: the tab switcher brings its own bottom margin. */}
                <div id={PANEL_ID} className="flex flex-col gap-6">
                    {children}
                </div>
            </div>
        </>
    );
}
